import { Button } from "@/dom/components/Button/Button";
import { Panel } from "@/dom/components/Panel/Panel";
import { PanelBody } from "@/dom/components/Panel/PanelBody";
import { PanelHeader } from "@/dom/components/Panel/PanelHeader";
import { Row } from "@/dom/components/Panel/Row";
import { useFurnitureManagementState } from "@/state/furnitureManagementState";
import { useFurnitureWithModel, useResourcesState } from "@/state/resourcesState";
import { formatPrice } from "@/utils/string";
import React, { useMemo } from "react";
import { DimensionsSection } from "./DimensionsSection";
import { MaterialSection } from "./MaterialsSection";
import { FurnitureElementsView } from "./FurnitureElementsView";
import { Icon } from "@/dom/components/Icon/Icon";
import { VariantParametersSection } from "./VariantParametersSection";

type Props = {
    selectedFurnitureInstanceId: string;
    onBack: () => void;
    onDelete: () => void;
    onOtherVariantSelected: (otherVariantId: string) => void;
};

export const FurnitureInstanceDetails: React.FC<Props> = ({
    selectedFurnitureInstanceId,
    onBack,
    onDelete,
    onOtherVariantSelected,
}) => {
    const furnitureInstance = useFurnitureManagementState(
        (state) => state.furnitureInstances[selectedFurnitureInstanceId]
    );

    const allFurniture = useResourcesState((state) => state.furniture);

    const furnitureVariant = useFurnitureWithModel({ id: furnitureInstance.furnitureId });
    const materials = useResourcesState((state) => state.materials);

    const setMaterialSlotMaterialName = useFurnitureManagementState(
        (state) => state.setMaterialSlotMaterialName
    );
    const setMaterialSlotMaterialColor = useFurnitureManagementState(
        (state) => state.setMaterialSlotMaterialColor
    );

    const otherVariants = useMemo(() => {
        if (!furnitureVariant || furnitureVariant.otherVariantIds.length === 0) {
            return [];
        }

        return Object.values(allFurniture).filter((otherFurniture) =>
            furnitureVariant.otherVariantIds.includes(otherFurniture.id)
        );
    }, [furnitureVariant, allFurniture]);

    if (!furnitureVariant) {
        return (
            <Panel>
                <PanelHeader title="Загрузка..." />
            </Panel>
        );
    }

    return (
        <Panel>
            <PanelHeader
                title={
                    <Row justify="space-between">
                        <span>{furnitureVariant.data.name}</span>
                        <span>{formatPrice(furnitureVariant.data.price)}</span>
                    </Row>
                }
                onBack={onBack}
            />
            <PanelBody>
                <Row justify="flex-end">
                    <Button variant="text" size="small" onClick={onDelete}>
                        <Icon icon="trasn-one" />
                        Удалить
                    </Button>
                </Row>
                <DimensionsSection
                    dimensions={furnitureVariant.data.dimensions}
                    otherVariants={otherVariants}
                    onOtherVariantSelected={onOtherVariantSelected}
                />
                <MaterialSection
                    instance={furnitureInstance}
                    furniture={furnitureVariant}
                    materials={materials}
                    onMaterialNameChange={(slotId, materialName) =>
                        setMaterialSlotMaterialName(
                            selectedFurnitureInstanceId,
                            slotId,
                            materialName
                        )
                    }
                    onMaterialColorChange={(slotId, color) =>
                        setMaterialSlotMaterialColor(selectedFurnitureInstanceId, slotId, color)
                    }
                />
                <VariantParametersSection
                    furniture={furnitureVariant}
                    otherVariants={otherVariants}
                    onOtherVariantSelected={onOtherVariantSelected}
                />
                <FurnitureElementsView instance={furnitureInstance} />
            </PanelBody>
        </Panel>
    );
};
